// Messaging query layer. Pairs with `lib/views/messaging.ts` (server fetchers
// used during SSR prefetch) and `lib/mutations/messaging.ts`.
//
// No 'use client' directive: the hooks are React hooks called from client
// components, but server code (pages, layouts) imports the keys to wire up
// prefetches.

import {
  keepPreviousData,
  useQuery,
  useInfiniteQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { ConversationId, UserId } from '@leanin/shared/ids';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';

// Directory search result shape for the messages "new message" / "add to
// group" pickers. Same shape the previous `searchDirectoryAction` returned.
export type DirectoryMemberRow = {
  id: string;
  name: string;
  subtitle: string;
  photo?: string;
};

export type ConversationSummary = components['schemas']['ConversationSummary'];
export type ConversationsPage = components['schemas']['ConversationsPage'];
export type ConversationDetail = components['schemas']['ConversationDetail'];
export type ConversationDetailEnvelope =
  components['schemas']['ConversationDetailEnvelope'];
export type ConversationScopeCounts =
  components['schemas']['ConversationScopeCounts'];
export type MessageView = components['schemas']['MessageView'];
export type MessagesPage = components['schemas']['MessagesPage'];
export type BlocksPage = components['schemas']['BlocksPage'];

export type ListConversationsInput = {
  pageSize?: number;
  kind?: 'dm' | 'group';
  q?: string;
  unreadOnly?: boolean;
};

// New-message thread polling cadence. 5s matches the existing SSE-replacement
// budget — fast enough that a reply lands in roughly one breath, slow enough
// that an idle thread doesn't hammer the API. `staleTime: 0` on the messages
// query keeps `refetchInterval` honest.
export const MESSAGES_POLL_INTERVAL_MS = 5_000;

// ---- Query keys ----

export const messagingKeys = {
  all: ['messaging'] as const,
  conversations: () => [...messagingKeys.all, 'conversations'] as const,
  conversationsList: (params: ListConversationsInput) =>
    [...messagingKeys.conversations(), 'list', params] as const,
  conversation: (id: ConversationId) =>
    [...messagingKeys.all, 'conversation', id] as const,
  conversationScopeCounts: (q?: string) =>
    [...messagingKeys.all, 'conversation-scope-counts', q ?? null] as const,
  // PageSize intentionally NOT in the key — it's a fetch parameter, not part
  // of the conversation's message-thread identity. Including it would split
  // the cache and break prefix matching for cross-variant operations like
  // optimistic message append.
  messages: (conversationId: ConversationId) =>
    [...messagingKeys.all, 'messages', conversationId] as const,
  blocks: () => [...messagingKeys.all, 'blocks'] as const,
};

// ---- Client fetchers ----

export async function fetchConversations(
  params: ListConversationsInput & { cursor?: string },
): Promise<ConversationsPage> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/conversations', {
    params: {
      query: {
        pageSize: params.pageSize,
        cursor: params.cursor,
        kind: params.kind,
        q: params.q,
        ...(params.unreadOnly !== undefined
          ? { unread: params.unreadOnly ? 'true' : 'false' }
          : {}),
      },
    },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchConversation(
  id: ConversationId,
): Promise<ConversationDetailEnvelope> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/conversations/{id}', {
    params: { path: { id } },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchConversationMessages(params: {
  conversationId: ConversationId;
  cursor?: string;
  pageSize?: number;
}): Promise<MessagesPage> {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/conversations/{id}/messages',
    {
      params: {
        path: { id: params.conversationId },
        query: {
          pageSize: params.pageSize,
          cursor: params.cursor,
        },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchConversationScopeCounts(
  q?: string,
): Promise<ConversationScopeCounts> {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/conversations/scope-counts',
    {
      params: {
        query: {
          ...(q !== undefined ? { q } : {}),
        },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchBlocks(input: {
  cursor?: string;
  pageSize?: number;
} = {}): Promise<BlocksPage> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/blocks', {
    params: {
      query: {
        pageSize: input.pageSize,
        cursor: input.cursor,
      },
    },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

// ---- Hooks ----

export function useConversations(
  input: ListConversationsInput,
  options?: Omit<
    UseQueryOptions<ConversationsPage, Error, ConversationsPage>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: messagingKeys.conversationsList(input),
    queryFn: () => fetchConversations(input),
    ...options,
  });
}

export function useInfiniteConversations(input: ListConversationsInput) {
  return useInfiniteQuery({
    queryKey: messagingKeys.conversationsList(input),
    queryFn: ({ pageParam }) =>
      fetchConversations({ ...input, cursor: pageParam ?? undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

export function useConversation(id: ConversationId | null) {
  return useQuery({
    queryKey: id !== null ? messagingKeys.conversation(id) : ['messaging', 'conversation', null],
    queryFn: () => {
      if (id === null) throw new Error('useConversation: id is null');
      return fetchConversation(id);
    },
    enabled: id !== null,
  });
}

// Paginated messages for a thread. Polling replaces the previous SSE stream:
// `refetchInterval: MESSAGES_POLL_INTERVAL_MS` re-fetches the first page on a
// 5s cadence so newly-delivered peer messages appear without per-thread SSE
// wiring. The polling pauses automatically when the tab is hidden
// (`refetchIntervalInBackground` is left at its default `false`).
export function useInfiniteMessages(
  conversationId: ConversationId | null,
  options?: { pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 50;
  return useInfiniteQuery({
    queryKey:
      conversationId !== null
        ? messagingKeys.messages(conversationId)
        : ['messaging', 'messages', null],
    queryFn: ({ pageParam }) => {
      if (conversationId === null) {
        throw new Error('useInfiniteMessages: conversationId is null');
      }
      return fetchConversationMessages({
        conversationId,
        pageSize,
        cursor: pageParam ?? undefined,
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: conversationId !== null,
    staleTime: 0,
    refetchInterval: conversationId !== null ? MESSAGES_POLL_INTERVAL_MS : false,
  });
}

export function useConversationScopeCounts(q?: string) {
  return useQuery({
    queryKey: messagingKeys.conversationScopeCounts(q),
    queryFn: () => fetchConversationScopeCounts(q),
  });
}

// Directory search for the messages pickers. The /members endpoint with
// `sort: 'connection'` ranks people the viewer follows or who follow them
// ahead of strangers, and the server applies the same reachability +
// not-blocked filter the DM gate does — so every row returned here is safe
// to start a 1:1 with.
export async function fetchDirectory(
  query: string,
): Promise<readonly DirectoryMemberRow[]> {
  const trimmed = query.trim();
  const client = getApiClient();
  const { data } = await client.GET('/api/v1/members', {
    params: {
      query: {
        pageSize: 20,
        sort: 'connection',
        ...(trimmed.length > 0 ? { q: trimmed } : {}),
      },
    },
  });
  if (data === undefined) return [];
  return data.items.map((m) => {
    const subtitle = [m.jobTitle, m.company]
      .filter((s): s is string => typeof s === 'string' && s.length > 0)
      .join(' · ');
    const out: DirectoryMemberRow = {
      id: m.id,
      name: [m.firstName, m.lastName]
        .filter((s): s is string => typeof s === 'string' && s.length > 0)
        .join(' '),
      subtitle,
    };
    if (m.photoUrl !== undefined && m.photoUrl !== null) {
      out.photo = m.photoUrl;
    }
    return out;
  });
}

export function useDirectory(query: string) {
  return useQuery({
    queryKey: [...messagingKeys.all, 'directory', query.trim()] as const,
    queryFn: () => fetchDirectory(query),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useBlocks(input: { cursor?: string; pageSize?: number } = {}) {
  return useQuery({
    queryKey: [...messagingKeys.blocks(), input] as const,
    queryFn: () => fetchBlocks(input),
  });
}

// Re-export the messaging-domain id types so consumers can import either ids
// or query helpers from a single place when convenient.
export type { ConversationId, UserId };
